import { useState } from 'react';
import { Skeleton, Switch } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFloppyDisk } from '@fortawesome/free-solid-svg-icons';
import { useMessagingSettingsQuery, useUpdateMessagingSettings } from '../../queries/messagingSettings';
import { Field, FormField, Select, TextArea } from '../../components';
import { Card, PrimaryBtn, RowSub, RowTitle, SaveBar, SetBlock, SetHead, SetRow, SettingsStack } from './styled';

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' }
];

const TIMES = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  const m = i % 2 ? '30' : '00';
  return { value: `${String(h).padStart(2, '0')}:${m}`, label: `${h % 12 || 12}:${m} ${h < 12 ? 'AM' : 'PM'}` };
});

const toKeywords = (text) =>
  text
    .split(',')
    .map((k) => k.trim())
    .filter(Boolean);

function SettingsForm({ settings, canManage }) {
  const [autoReply, setAutoReply] = useState(settings.autoReply);
  const [quietHours, setQuietHours] = useState(settings.quietHours);
  const [keywords, setKeywords] = useState(settings.compliance.optOutKeywords.join(', '));
  const save = useUpdateMessagingSettings();

  const patchQuiet = (key) => (e) => setQuietHours((q) => ({ ...q, [key]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    save.mutate({ autoReply, quietHours, compliance: { optOutKeywords: toKeywords(keywords) } });
  };

  return (
    <SettingsStack onSubmit={submit}>
      <Card>
        <SetHead>Auto-reply</SetHead>
        <SetRow>
          <div>
            <RowTitle id="auto-reply">Enable auto-reply</RowTitle>
            <RowSub>Answer messages received outside business hours with an acknowledgement.</RowSub>
          </div>
          <Switch
            aria-labelledby="auto-reply"
            checked={autoReply.enabled}
            disabled={!canManage}
            onChange={(enabled) => setAutoReply((a) => ({ ...a, enabled }))}
          />
        </SetRow>
        {autoReply.enabled && (
          <SetBlock>
            <FormField>
              <label htmlFor="auto-reply-msg">
                <RowTitle>Auto-reply message</RowTitle>
              </label>
              <TextArea
                id="auto-reply-msg"
                maxLength={1000}
                value={autoReply.message}
                disabled={!canManage}
                onChange={(e) => setAutoReply((a) => ({ ...a, message: e.target.value }))}
              />
            </FormField>
          </SetBlock>
        )}
      </Card>

      <Card>
        <SetHead>Quiet hours</SetHead>
        <SetRow>
          <div>
            <RowTitle id="quiet-hours">Enable quiet hours</RowTitle>
            <RowSub>Hold outbound texts and AI suggestions during these hours.</RowSub>
          </div>
          <Switch
            aria-labelledby="quiet-hours"
            checked={quietHours.enabled}
            disabled={!canManage}
            onChange={(enabled) => setQuietHours((q) => ({ ...q, enabled }))}
          />
        </SetRow>
        {[
          ['start', 'Start time', TIMES],
          ['end', 'End time', TIMES],
          ['timezone', 'Timezone', TIMEZONES]
        ].map(([key, label, options]) => (
          <SetRow key={key} $tight>
            <label htmlFor={`quiet-${key}`}>{label}</label>
            <Select
              id={`quiet-${key}`}
              value={quietHours[key]}
              disabled={!canManage || !quietHours.enabled}
              onChange={patchQuiet(key)}
            >
              {options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </SetRow>
        ))}
      </Card>

      <Card>
        <SetHead>Compliance</SetHead>
        <SetBlock>
          <label htmlFor="opt-out">
            <RowTitle>Opt-out keywords</RowTitle>
          </label>
          <RowSub style={{ marginBottom: 10 }}>Any of these replies stops all SMS to that contact immediately.</RowSub>
          <Field
            id="opt-out"
            value={keywords}
            disabled={!canManage}
            placeholder="STOP, UNSUBSCRIBE, CANCEL"
            onChange={(e) => setKeywords(e.target.value)}
          />
        </SetBlock>
      </Card>

      {canManage && (
        <SaveBar>
          <span>Applies to every outbound text on all lines.</span>
          <PrimaryBtn type="submit" disabled={save.isPending}>
            <FontAwesomeIcon icon={faFloppyDisk} />
            {save.isPending ? 'Saving…' : 'Save settings'}
          </PrimaryBtn>
        </SaveBar>
      )}
    </SettingsStack>
  );
}

export default function SettingsTab({ canManage }) {
  const { data, isLoading } = useMessagingSettingsQuery();
  if (isLoading || !data?.settings) return <Skeleton active paragraph={{ rows: 10 }} />;
  return <SettingsForm settings={data.settings} canManage={canManage} />;
}
